import { deltaToPlainText } from "util/richText";
import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { CLICKED_SEND_INVOICE_FROM_EMAIL_PREVIEW_MODAL } from "constants/analyticsEvents";
import { Formik } from "formik";
import { useAnalytics } from "hooks/useAnalytics";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useHistory, useParams } from "react-router-dom";
import {
  useGetEntityInvoiceQuery,
  useGetInvoiceSettingsQuery,
  useSendInvoiceMutation,
  useGetInvoiceCustomerQuery,
} from "store/apis/invoices";
import { SelecteFile } from "types/Models/documents";
import { Entity } from "types/Models/entity";
import { currency } from "utils/Currency";
import { SendInvoice } from "./SendInvoice";
import { USD } from "constants/currency";
import { formatNumber } from "utils/formatNumber";
import { Invoice, InvoiceSettingsType } from "types/Models/invoice";

export type SendInvoiceForm = {
  attachments: File[];
  subject: string;
  body: string;
  attachWform: boolean;
  wform: SelecteFile | null;
  from_email_type: string;
  cc_emails: string[];
  recipients: string[];
};

export const invoiceBody = ({
  invoice,
  entity,
  invoiceSetting,
  isSentInvoiceEditFlow,
  currencyCode,
}: {
  invoice: Invoice;
  entity: Entity;
  invoiceSetting: InvoiceSettingsType;
  isSentInvoiceEditFlow: boolean;
  currencyCode?: string;
}) => {
  const addUpdatedInEmail = isSentInvoiceEditFlow ? "updated " : "";

  return `Hi,

Please find ${addUpdatedInEmail}attached Invoice ${
    invoice.invoice_number
  } for the amount of ${
    currencyCode === USD
      ? currency({
          amount: invoice.due_balance || 0,
        })
      : `${formatNumber(Number(invoice.due_balance), 2)} ${currencyCode}`
  } from ${entity.name}, feel free to reach out if you have any questions.
${
  invoice.payment_link
    ? `
You can make the payment by opening this link - ${invoice.payment_link}.
`
    : ""
}${
    invoiceSetting?.payment_info
      ? `
Payment Instructions:
${deltaToPlainText(invoiceSetting.payment_info)}
`
      : ""
  }
Thank you for your prompt payment.

Best Regards,
${entity.name}
${invoiceSetting?.email}`;
};

export const invoiceSubject = ({
  entity,
  invoice,
  isReminder,
  isSentInvoiceEditFlow,
}: {
  entity: Entity;
  invoice: Invoice;
  isReminder?: boolean;
  isSentInvoiceEditFlow?: boolean;
}) => {
  let addUpdatedInSubject = isSentInvoiceEditFlow ? "Updated: " : "";
  if (isReminder) {
    addUpdatedInSubject += "Reminder: ";
  }

  return (
    addUpdatedInSubject +
    (invoice?.title || `You have a new invoice from ${entity.name}`)
  );
};

export const SendInvoiceModal = ({
  isOpen,
  close,
  invoiceIdFromProp,
  isReminder,
}: {
  isOpen: boolean;
  close: () => void;
  invoiceIdFromProp?: string;
  isReminder?: boolean;
}) => {
  const group = useCurrentGroupContext();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { successToast, alertToast } = useToast();
  const history = useHistory();
  const { trackEvent } = useAnalytics();
  const invoiceUuid = invoiceIdFromProp || invoiceId;
  const query = useQuery();
  const editFlow = query.get("editflow");
  const entityId = useCurrentEntityId();
  const [sendInvoice, { isLoading: sendingInvoice }] = useSendInvoiceMutation();

  const { data: invoiceSetting } = useGetInvoiceSettingsQuery(
    {
      groupId: group.uuid,
      entityId,
    },
    {
      skip: !group.uuid || !entityId,
    }
  );

  const { data: invoice, isLoading: invoiceLoading } = useGetEntityInvoiceQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      invoiceId: invoiceUuid,
    },
    { skip: !group?.uuid || !entityId }
  );

  const {
    data: customerData,
    isLoading: customersLoading,
    isFetching: customersFetching,
  } = useGetInvoiceCustomerQuery(
    {
      groupId: group.uuid,
      entityId: entityId!,
      customerId: invoice?.customer?.customer_id!,
    },
    {
      skip: !group.uuid || !entityId || !invoice?.customer?.customer_id,
    }
  );

  const isSentInvoiceEditFlow =
    (invoice?.status === "DUE" || invoice?.status === "OVERDUE") &&
    Boolean(editFlow);

  const { link } = useConstructInternalLink();

  const onSendInvoice = async (values: {
    attachments: File[];
    body: string;
    subject: string;
    cc_emails: string[];
    from_email_type: string;
    recipients: string[];
  }) => {
    trackEvent(CLICKED_SEND_INVOICE_FROM_EMAIL_PREVIEW_MODAL);
    if (group?.uuid && entityId) {
      try {
        await sendInvoice({
          groupId: group.uuid,
          entityId: entityId,
          invoiceId: invoiceUuid,
          payload: {
            subject: values.subject,
            body: values.body,
            attachments: values.attachments,
            from_email_type: values.from_email_type,
            recipients: values.recipients.join(","),
          },
        }).unwrap();
        successToast({ message: "Invoice has been sent." });
        if (Boolean(invoiceIdFromProp)) {
          close();
          return;
        }
        history.push(link("/books/invoices-and-customers"));
      } catch (error: any) {
        alertToast({ message: error?.data?.error?.message }, error as Error);
      }
    }
  };

  const entity = group.entities.find((entity) => entity.uuid === entityId);

  if (!invoice || !entity || invoiceLoading || !invoiceSetting) {
    return (
      <Modal.Root>
        <Modal.Content>
          <Modal.Body>
            <div className="t-flex t-w-full t-h-full t-justify-center t-items-center">
              <Loader size="small" />
            </div>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  const defaultBody = invoiceBody({
    entity,
    invoice,
    invoiceSetting,
    isSentInvoiceEditFlow,
    currencyCode: invoice?.currency,
  });

  const subject = invoiceSubject({
    entity,
    invoice,
    isSentInvoiceEditFlow,
    isReminder,
  });

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content>
        <Formik<SendInvoiceForm>
          onSubmit={onSendInvoice}
          enableReinitialize
          initialValues={{
            attachments: [] as File[],
            subject: subject,
            body: defaultBody,
            attachWform: invoice?.should_send_w_form,
            wform: invoice?.w_form || null,
            from_email_type: "INKLE",
            cc_emails: [],
            recipients:
              customerData?.recipient_emails ||
              invoice?.customer?.recipient_emails ||
              (invoice?.customer?.email ? [invoice.customer.email] : []),
          }}
        >
          {({ submitForm }) => {
            return (
              <>
                <Modal.Header>
                  <Modal.Title>Send Invoice</Modal.Title>
                  <Modal.Close />
                </Modal.Header>

                <Modal.Body>
                  <SendInvoice invoice={invoice} />
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Back</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    isLoading={sendingInvoice}
                    disabled={sendingInvoice}
                    type="submit"
                  >
                    Send invoice
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
